import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteContactsApplicationErrors,
  BulkDeleteContactsOptions,
  BulkDeleteContactsResponse,
  BulkLabelAndUnlabelContactsApplicationErrors,
  BulkLabelAndUnlabelContactsOptions,
  BulkLabelAndUnlabelContactsResponse,
  BulkUpdateContactsOptions,
  BulkUpdateContactsResponse,
  Contact,
  ContactCreatedEnvelope,
  ContactDeletedEnvelope,
  ContactInfo,
  ContactMergedEnvelope,
  ContactQuery,
  ContactUpdatedEnvelope,
  ContactsQueryBuilder,
  CreateContactApplicationErrors,
  CreateContactOptions,
  CreateContactResponse,
  DeleteContactApplicationErrors,
  GetContactApplicationErrors,
  GetContactOptions,
  LabelContactApplicationErrors,
  LabelContactResponse,
  ListContactsApplicationErrors,
  ListContactsOptions,
  ListContactsResponse,
  ListFacetsOptions,
  ListFacetsResponse,
  MergeContactsApplicationErrors,
  MergeContactsOptions,
  MergeContactsResponse,
  PreviewMergeContactsApplicationErrors,
  PreviewMergeContactsOptions,
  PreviewMergeContactsResponse,
  QueryContactsApplicationErrors,
  QueryContactsOptions,
  QueryContactsResponse,
  QueryFacetsOptions,
  QueryFacetsResponse,
  UnlabelContactApplicationErrors,
  UnlabelContactResponse,
  UpdateContactApplicationErrors,
  UpdateContactOptions,
  UpdateContactResponse,
  bulkDeleteContacts as universalBulkDeleteContacts,
  bulkLabelAndUnlabelContacts as universalBulkLabelAndUnlabelContacts,
  bulkUpdateContacts as universalBulkUpdateContacts,
  createContact as universalCreateContact,
  deleteContact as universalDeleteContact,
  getContact as universalGetContact,
  labelContact as universalLabelContact,
  listContacts as universalListContacts,
  listFacets as universalListFacets,
  mergeContacts as universalMergeContacts,
  previewMergeContacts as universalPreviewMergeContacts,
  queryContacts as universalQueryContacts,
  queryFacets as universalQueryFacets,
  typedQueryContacts as universalTypedQueryContacts,
  unlabelContact as universalUnlabelContact,
  updateContact as universalUpdateContact,
} from './contacts-v4-contact-contacts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/crm' };

export function createContact(httpClient: HttpClient): CreateContactSignature {
  return (info: ContactInfo, options?: CreateContactOptions) =>
    universalCreateContact(
      info,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateContactSignature {
  /**
   * Creates a new contact.
   *
   * The `info` object must include at least one of the following:
   * - Name
   * - Phone number
   * - Email address
   *
   * By default, if the call contains an email already in use by another contact,
   * the new contact won't be created. To override this behavior, set `allowDuplicates` to `true`.
   * @param - Contact info.
   * @param - Create contact options.
   * @returns Contact.
   */
  (info: ContactInfo, options?: CreateContactOptions): Promise<
    NonNullablePaths<
      CreateContactResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: CreateContactApplicationErrors;
    }
  >;
}

export function updateContact(httpClient: HttpClient): UpdateContactSignature {
  return (
    contactId: string,
    info: ContactInfo,
    revision: number,
    options?: UpdateContactOptions
  ) =>
    universalUpdateContact(
      contactId,
      info,
      revision,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateContactSignature {
  /**
   * Updates a contact.
   *
   * Each time the contact is updated,
   * `revision` increments by 1.
   * The existing `revision` must be included when updating the contact.
   * This ensures you're working with the latest contact information,
   * and it prevents unintended overwrites.
   *
   * You can't call Update Contact to update the `primaryEmail` of a contact who is also a member.
   * Instead, use the Members API to update the member's login email, which will also update the contact's primary email.
   *
   * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers. Make sure to specify the contact ID when calling Update Contact.
   * @param - ID of the contact to update.
   * @param - Contact info.
   * @param - Revision number.
   * When updating, include the existing `revision`
   * to prevent conflicting updates.
   * @param - Contact update options.
   * @returns Updated contact.
   */
  (
    contactId: string,
    info: ContactInfo,
    revision: number,
    options?: UpdateContactOptions
  ): Promise<
    NonNullablePaths<
      UpdateContactResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: UpdateContactApplicationErrors;
    }
  >;
}

export function mergeContacts(httpClient: HttpClient): MergeContactsSignature {
  return (
    targetContactId: string,
    targetContactRevision: number,
    options?: MergeContactsOptions
  ) =>
    universalMergeContacts(
      targetContactId,
      targetContactRevision,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MergeContactsSignature {
  /**
   * Merges source contacts into a target contact.
   *
   * Merging contacts has the following effects on the target contact:
   *
   * - No target contact data is overwritten or deleted.
   * - Arrays (emails, phone numbers, addresses, and labels) from the source contacts are added to the target contact's arrays.
   * - If you merge more than one source contact, the 1st source contact specified is added first, then the 2nd, and so on.
   * - If there is duplicate information between the target contact and the source contact, the information isn't duplicated in the target contact's arrays.
   *
   * <blockquote class="important">
   *
   * __Important:__
   * Merges cannot be undone.
   * In REST, call Preview Merge Contacts to test before merging.
   *
   * </blockquote>
   *
   * When you merge contacts, source contacts are typically deleted.
   * However, if a source contact is a site member or a Wix user,
   * the merge will fail because site members and Wix users can't be deleted.
   * This means that site members and Wix users can only be specified as target contacts in a merge.
   *
   * After merging, calling Get Contact with a deleted source contact ID returns the target contact ID.
   * Specifying a deleted source contact ID is not supported on any other method.
   *
   * When contacts are merged:
   *
   * - The Contact Merged event is triggered.
   * - The Contact Updated event is triggered for the target contact. `originatedFrom` is set to `merge`.
   * - The Contact Deleted event is triggered for each source contact. `originatedFrom` is set to `merge`.
   * @param - Target contact ID.
   * @param - Target contact revision number, which increments by 1 each time the contact is updated.
   * To prevent conflicting changes,
   * the target contact's current `revision` must be specified.
   * @param - Merge contacts options.
   */
  (
    targetContactId: string,
    targetContactRevision: number,
    options?: MergeContactsOptions
  ): Promise<
    NonNullablePaths<
      MergeContactsResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: MergeContactsApplicationErrors;
    }
  >;
}

export function previewMergeContacts(
  httpClient: HttpClient
): PreviewMergeContactsSignature {
  return (targetContactId: string, options?: PreviewMergeContactsOptions) =>
    universalPreviewMergeContacts(
      targetContactId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PreviewMergeContactsSignature {
  /**
   * Previews merging source contacts into a target contact.
   *
   * This method performs a dry run without deleting or updating any contacts.
   * A preview of the updated target contact is returned,
   * but no data is changed in the contact list.
   *
   * To perform the actual merge, use Merge Contacts.
   * @param - Target contact ID.
   * @param - Preview merge contacts options.
   */
  (targetContactId: string, options?: PreviewMergeContactsOptions): Promise<
    NonNullablePaths<
      PreviewMergeContactsResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: PreviewMergeContactsApplicationErrors;
    }
  >;
}

export function deleteContact(httpClient: HttpClient): DeleteContactSignature {
  return (contactId: string) =>
    universalDeleteContact(
      contactId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteContactSignature {
  /**
   * Deletes a contact.
   *
   * Deleting a contact permanently removes it from the contact list.
   * If a contact is also a site member or Wix user, or has a valid billing subscription,
   * the contact can't be deleted. The related site member or Wix user must first be deleted
   * and any valid billing subscriptions must be canceled, before the contact can be deleted.
   *
   * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
   * Make sure to specify the contact ID when calling Delete Contact.
   * @param - ID of the contact to delete.
   */
  (contactId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteContactApplicationErrors;
    }
  >;
}

export function labelContact(httpClient: HttpClient): LabelContactSignature {
  return (contactId: string, labelKeys: string[]) =>
    universalLabelContact(
      contactId,
      labelKeys,
      // @ts-ignore
      { httpClient }
    );
}

interface LabelContactSignature {
  /**
   * Adds labels to a contact.
   *
   * Use the Labels API to create or retrieve labels.
   *
   * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
   * Make sure to specify the contact ID when calling Label Contact.
   * @param - ID of the contact to add labels to.
   * @param - List of label keys to add to the contact.
   *
   * Label keys must exist to be added to the contact.
   * Use the Labels API to create or retrieve labels.
   * @returns Updated contact.
   */
  (contactId: string, labelKeys: string[]): Promise<
    NonNullablePaths<
      LabelContactResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: LabelContactApplicationErrors;
    }
  >;
}

export function unlabelContact(
  httpClient: HttpClient
): UnlabelContactSignature {
  return (contactId: string, labelKeys: string[]) =>
    universalUnlabelContact(
      contactId,
      labelKeys,
      // @ts-ignore
      { httpClient }
    );
}

interface UnlabelContactSignature {
  /**
   * Removes labels from a contact.
   *
   * If a label is no longer needed
   * and you want to remove it from all contacts,
   * you can delete by calling Delete Label from the Labels API.
   *
   * Members are typically linked to contacts, and while they share a relationship, the member ID and contact ID are distinct identifiers.
   * Make sure to specify the contact ID when calling Unlabel Contact.
   * @param - ID of the contact to remove labels from.
   * @param - List of label keys to remove from the contact.
   * @returns Updated contact.
   */
  (contactId: string, labelKeys: string[]): Promise<
    NonNullablePaths<
      UnlabelContactResponse,
      | `contact._id`
      | `contact.revision`
      | `contact.source.sourceType`
      | `contact.lastActivity.activityType`
      | `contact.info.emails.items`
      | `contact.info.emails.items.${number}.tag`
      | `contact.info.emails.items.${number}.email`
      | `contact.info.phones.items`
      | `contact.info.phones.items.${number}.tag`
      | `contact.info.phones.items.${number}.phone`
      | `contact.info.addresses.items`
      | `contact.info.addresses.items.${number}.tag`
      | `contact.info.addresses.items.${number}.address.streetAddress.number`
      | `contact.info.addresses.items.${number}.address.streetAddress.name`
      | `contact.info.labelKeys.items`
      | `contact.primaryEmail.subscriptionStatus`
      | `contact.primaryEmail.deliverabilityStatus`
      | `contact.primaryPhone.subscriptionStatus`,
      9
    > & {
      __applicationErrorsType?: UnlabelContactApplicationErrors;
    }
  >;
}

export function listContacts(httpClient: HttpClient): ListContactsSignature {
  return (options?: ListContactsOptions) =>
    universalListContacts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListContactsSignature {
  /**
   * Retrieves a list of up to 1,000 contacts per request.
   * @param - Object containing list of options for retrieving contacts.
   * @returns List of contacts.
   */
  (options?: ListContactsOptions): Promise<
    NonNullablePaths<
      ListContactsResponse,
      | `contacts`
      | `contacts.${number}._id`
      | `contacts.${number}.revision`
      | `contacts.${number}.source.sourceType`
      | `contacts.${number}.lastActivity.activityType`
      | `contacts.${number}.primaryEmail.subscriptionStatus`
      | `contacts.${number}.primaryEmail.deliverabilityStatus`
      | `contacts.${number}.primaryPhone.subscriptionStatus`,
      5
    > & {
      __applicationErrorsType?: ListContactsApplicationErrors;
    }
  >;
}

export function queryContacts(httpClient: HttpClient): QueryContactsSignature {
  return (options?: QueryContactsOptions) =>
    universalQueryContacts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryContactsSignature {
  /**
   * Creates a query to retrieve a list of contacts.
   *
   * The `queryContacts()` function builds a query to retrieve a list of contacts and returns a `ContactsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `ContactsQueryBuilder` functions onto the query. `ContactsQueryBuilder` functions enable you to sort, filter, and control the results `queryContacts()` returns.
   *
   * `queryContacts()` runs with these `ContactsQueryBuilder` defaults, which you can override:
   *
   * - `skip(0)`
   * - `limit(50)`
   * - `descending("_createdDate")`
   *
   * The functions that are chained to `queryContacts()` are applied in the order they are called. For example, if you apply `ascending('info.company')` and then `descending('info.name.last')`, the results are sorted first by the company name, and then, if there are multiple results with the same company, the items are sorted by last name.
   * @param - Query contact options.
   */
  (options?: QueryContactsOptions): ContactsQueryBuilder;
}

export function typedQueryContacts(
  httpClient: HttpClient
): TypedQueryContactsSignature {
  return (query: ContactQuery, options?: QueryContactsOptions) =>
    universalTypedQueryContacts(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryContactsSignature {
  /** @returns List of contacts. */
  (query: ContactQuery, options?: QueryContactsOptions): Promise<
    NonNullablePaths<
      QueryContactsResponse,
      | `contacts`
      | `contacts.${number}._id`
      | `contacts.${number}.revision`
      | `contacts.${number}.source.sourceType`
      | `contacts.${number}.lastActivity.activityType`
      | `contacts.${number}.primaryEmail.subscriptionStatus`
      | `contacts.${number}.primaryEmail.deliverabilityStatus`
      | `contacts.${number}.primaryPhone.subscriptionStatus`,
      5
    > & {
      __applicationErrorsType?: QueryContactsApplicationErrors;
    }
  >;
}

export function listFacets(httpClient: HttpClient): ListFacetsSignature {
  return (options?: ListFacetsOptions) =>
    universalListFacets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFacetsSignature {
  /**
   * Lists facets from the site’s contact list.
   * Facets include labels and subscription statuses.
   * @param - List facets options.
   */
  (options?: ListFacetsOptions): Promise<
    NonNullablePaths<
      ListFacetsResponse,
      `facets` | `facets.${number}.facetType`,
      4
    >
  >;
}

export function queryFacets(httpClient: HttpClient): QueryFacetsSignature {
  return (options?: QueryFacetsOptions) =>
    universalQueryFacets(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryFacetsSignature {
  /**
   * Retrieves facets from the site’s contact list by filter.
   * Facets include labels and subscription statuses.
   * @param - Query facets options.
   */
  (options?: QueryFacetsOptions): Promise<
    NonNullablePaths<
      QueryFacetsResponse,
      `facets` | `facets.${number}.facetType`,
      4
    >
  >;
}

export function bulkDeleteContacts(
  httpClient: HttpClient
): BulkDeleteContactsSignature {
  return (options?: BulkDeleteContactsOptions) =>
    universalBulkDeleteContacts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteContactsSignature {
  /**
   * Deletes multiple contacts.
   *
   * All contacts that meet the specified `filter` and `search` criteria are deleted.
   * The request should contain a `filter` value or a `search` value, or both.
   * To perform a dry run, call Query Contacts with the intended filter options.
   *
   * When this method is called, a bulk job is started and the job ID is returned.
   * The job might not complete right away, depending on its size.
   * The job's status can be retrieved with Get Bulk Job.
   *
   * The following errors might occur during the bulk processing and will appear in the bulk job:
   * - `CANNOT_DELETE_SITE_MEMBERS` - Contact is a site member and can't be deleted. Member must be deleted first.
   * - `CANNOT_DELETE_CONTACT_WITH_BILLING_SUBSCRIPTION` - Contact has a valid billing subscription and can't be deleted.
   * - `CANNOT_DELETE_MEMBER_OWNER_OR_CONTRIBUTOR` - Member is a Wix user and can't be deleted. This can happen only if the request indicated to delete the member.
   * - `FAILED_DELETE_CONTACT_AFTER_MEMBER_DELETION` - Member was deleted, but contact was not. This can happen only if the request indicated to delete the member.
   * - `FAILED_DELETE_CONTACT` - Contact could not be deleted.
   * @param - Bulk delete contacts options.
   */
  (options?: BulkDeleteContactsOptions): Promise<
    NonNullablePaths<BulkDeleteContactsResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkDeleteContactsApplicationErrors;
    }
  >;
}

export function bulkUpdateContacts(
  httpClient: HttpClient
): BulkUpdateContactsSignature {
  return (options?: BulkUpdateContactsOptions) =>
    universalBulkUpdateContacts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateContactsSignature {
  /**
   * Updates the specified properties for multiple contacts.
   * Fields that are included in fieldMask.paths are updated,
   * while all other fields stay the same.
   *
   * All contacts that meet the specified `filter` and `search` criteria are updated.
   * To perform a dry run, call Query Contacts with the intended filter options.
   *
   * When this method is used, a bulk job is started and the job ID is returned.
   * The job may not complete right away, depending on its size.
   * The job's status can be retrieved with Get Bulk Job.
   * @param - Bulk update contacts options.
   */
  (options?: BulkUpdateContactsOptions): Promise<
    NonNullablePaths<BulkUpdateContactsResponse, `jobId`, 2>
  >;
}

export function bulkLabelAndUnlabelContacts(
  httpClient: HttpClient
): BulkLabelAndUnlabelContactsSignature {
  return (options?: BulkLabelAndUnlabelContactsOptions) =>
    universalBulkLabelAndUnlabelContacts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkLabelAndUnlabelContactsSignature {
  /**
   * Adds and removes labels from multiple contacts.
   *
   * Labels are added to and removed from all contacts that meet the specified
   * `filter` and `search` criteria.
   * The request should specify a `filter` value, a `search` value, or both.
   * To perform a dry run, call Query Contacts with the intended filter options.
   *
   * When this method is used, a bulk job is started and the job ID is returned.
   * The job might not complete right away, depending on its size.
   * The job's status can be retrieved with Get Bulk Job.
   * @param - Bulk label and unlabel contacts options.
   */
  (options?: BulkLabelAndUnlabelContactsOptions): Promise<
    NonNullablePaths<BulkLabelAndUnlabelContactsResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkLabelAndUnlabelContactsApplicationErrors;
    }
  >;
}

export function getContact(httpClient: HttpClient): GetContactSignature {
  return (_id: string, options?: GetContactOptions) =>
    universalGetContact(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetContactSignature {
  /**
   * Retrieves a contact.
   *
   * #### Getting Merged Contacts
   *
   * When a source contact is merged
   * with a target contact, the source contact is deleted.
   * When calling Get Contact for a merged contact,
   * you can use the source or target contact ID.
   * In both bases, the target contact is returned.
   *
   * This is supported only when calling Get Contact on merged contacts.
   * Previously deleted source contact IDs can't be used for any other method.
   * @param - ID of the contact to retrieve.
   * @param - Get contact options.
   * @returns The requested contact.
   */
  (_id: string, options?: GetContactOptions): Promise<
    NonNullablePaths<
      Contact,
      | `_id`
      | `revision`
      | `source.sourceType`
      | `lastActivity.activityType`
      | `info.emails.items`
      | `info.emails.items.${number}.tag`
      | `info.emails.items.${number}.email`
      | `info.phones.items`
      | `info.phones.items.${number}.tag`
      | `info.phones.items.${number}.phone`
      | `info.addresses.items`
      | `info.addresses.items.${number}.tag`
      | `info.addresses.items.${number}.address.streetAddress.number`
      | `info.addresses.items.${number}.address.streetAddress.name`
      | `info.labelKeys.items`
      | `primaryEmail.subscriptionStatus`
      | `primaryEmail.deliverabilityStatus`
      | `primaryPhone.subscriptionStatus`,
      8
    > & {
      __applicationErrorsType?: GetContactApplicationErrors;
    }
  >;
}

export const onContactCreated = EventDefinition(
  'wix.contacts.v4.contact_created',
  true,
  (event: ContactCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastActivity.activityDate' },
            { path: 'entity.lastActivity.date' },
            { path: 'entity.memberInfo.signupDate' },
            { path: 'entity.memberInfo.sessionInfo.lastWebLogin' },
            { path: 'entity.memberInfo.sessionInfo.lastMobileLogin' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.picture' },
            { path: 'entity.info.picture.image' },
            { path: 'entity.memberInfo.profileInfo.photo' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.info.addresses.items.address' }],
        },
      ])
    )
)<ContactCreatedEnvelope>();
export const onContactDeleted = EventDefinition(
  'wix.contacts.v4.contact_deleted',
  true,
  (event: ContactDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.lastActivity.activityDate' },
            { path: 'undefined.lastActivity.date' },
            { path: 'undefined.memberInfo.signupDate' },
            { path: 'undefined.memberInfo.sessionInfo.lastWebLogin' },
            { path: 'undefined.memberInfo.sessionInfo.lastMobileLogin' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'undefined.picture' },
            { path: 'undefined.info.picture.image' },
            { path: 'undefined.memberInfo.profileInfo.photo' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'undefined.info.addresses.items.address' }],
        },
      ])
    )
)<ContactDeletedEnvelope>();
export const onContactMerged = EventDefinition(
  'wix.contacts.v4.contact_merged',
  true,
  (event: ContactMergedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.targetContact.createdDate' },
            { path: 'data.targetContact.updatedDate' },
            { path: 'data.targetContact.lastActivity.activityDate' },
            { path: 'data.targetContact.lastActivity.date' },
            { path: 'data.targetContact.memberInfo.signupDate' },
            { path: 'data.targetContact.memberInfo.sessionInfo.lastWebLogin' },
            {
              path: 'data.targetContact.memberInfo.sessionInfo.lastMobileLogin',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'data.targetContact.picture' },
            { path: 'data.targetContact.info.picture.image' },
            { path: 'data.targetContact.memberInfo.profileInfo.photo' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.targetContact.info.addresses.items.address' }],
        },
      ])
    )
)<ContactMergedEnvelope>();
export const onContactUpdated = EventDefinition(
  'wix.contacts.v4.contact_updated',
  true,
  (event: ContactUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastActivity.activityDate' },
            { path: 'entity.lastActivity.date' },
            { path: 'entity.memberInfo.signupDate' },
            { path: 'entity.memberInfo.sessionInfo.lastWebLogin' },
            { path: 'entity.memberInfo.sessionInfo.lastMobileLogin' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.lastActivity.activityDate' },
            { path: 'modifiedFields.lastActivity.date' },
            { path: 'modifiedFields.memberInfo.signupDate' },
            { path: 'modifiedFields.memberInfo.sessionInfo.lastWebLogin' },
            { path: 'modifiedFields.memberInfo.sessionInfo.lastMobileLogin' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.picture' },
            { path: 'entity.info.picture.image' },
            { path: 'entity.memberInfo.profileInfo.photo' },
            { path: 'modifiedFields.picture' },
            { path: 'modifiedFields.info.picture.image' },
            { path: 'modifiedFields.memberInfo.profileInfo.photo' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.info.addresses.items.address' },
            { path: 'modifiedFields.info.addresses.items.address' },
          ],
        },
      ])
    )
)<ContactUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  Action,
  ActionEvent,
  ActivityIcon,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  AddressTag,
  ApplicationError,
  AssigneesWrapper,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkAddSegmentToContactsRequest,
  BulkAddSegmentToContactsResponse,
  BulkDeleteContactsOptions,
  BulkDeleteContactsRequest,
  BulkDeleteContactsResponse,
  BulkLabelAndUnlabelContactsOptions,
  BulkLabelAndUnlabelContactsRequest,
  BulkLabelAndUnlabelContactsResponse,
  BulkRemoveSegmentFromContactsRequest,
  BulkRemoveSegmentFromContactsResponse,
  BulkUpdateContactsOptions,
  BulkUpdateContactsRequest,
  BulkUpdateContactsResponse,
  BulkUpsertContactsRequest,
  BulkUpsertContactsResponse,
  BulkUpsertContactsResponseMetadata,
  Contact,
  ContactActivity,
  ContactActivityType,
  ContactAddedToSegment,
  ContactAddress,
  ContactAddressesWrapper,
  ContactChanged,
  ContactCreatedEnvelope,
  ContactDeletedEnvelope,
  ContactEmail,
  ContactEmailSubscriptionUpdated,
  ContactEmailsWrapper,
  ContactFieldSet,
  ContactInfo,
  ContactMerged,
  ContactMergedEnvelope,
  ContactName,
  ContactPhone,
  ContactPhoneSubscriptionUpdated,
  ContactPhonesWrapper,
  ContactPicture,
  ContactPrimaryInfoUpdated,
  ContactQuerySpec,
  ContactRemovedFromSegment,
  ContactSource,
  ContactSourceType,
  ContactSubmitted,
  ContactUpdatedEnvelope,
  ContactsFacet,
  ContactsFacetType,
  ContactsQueryBuilder,
  ContactsQueryResult,
  CountContactsRequest,
  CountContactsResponse,
  CreateContactOptions,
  CreateContactRequest,
  CreateContactResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteContactRequest,
  DeleteContactResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DuplicateContactExists,
  EmailDeliverabilityStatus,
  EmailTag,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Error,
  EventMetadata,
  ExtendedFieldsWrapper,
  GeneratePictureUploadUrlRequest,
  GeneratePictureUploadUrlResponse,
  GetContactOptions,
  GetContactRequest,
  GetContactResponse,
  GetContactResponseType,
  GroupInfo,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImageProvider,
  Item,
  ItemMetadata,
  LabelAndUnlabelContactRequest,
  LabelAndUnlabelContactResponse,
  LabelContactRequest,
  LabelContactResponse,
  LabelsWrapper,
  LastActivityUpdate,
  ListContactIdsBySegmentRequest,
  ListContactIdsBySegmentResponse,
  ListContactsOptions,
  ListContactsRequest,
  ListContactsResponse,
  ListFacetsOptions,
  ListFacetsRequest,
  ListFacetsResponse,
  LocationsWrapper,
  MemberInfo,
  MemberStatus,
  MergeContactsOptions,
  MergeContactsRequest,
  MergeContactsResponse,
  MessageEnvelope,
  Metadata,
  Mode,
  Paging,
  PagingMetadata,
  PhoneDeliverabilityStatus,
  PhoneTag,
  PreviewMergeContactsOptions,
  PreviewMergeContactsRequest,
  PreviewMergeContactsResponse,
  PrimaryContactInfo,
  PrimaryEmail,
  PrimaryPhone,
  PrimarySubscriptionStatus,
  PrivacyStatus,
  ProfileInfo,
  Query,
  QueryContactsOptions,
  QueryContactsRequest,
  QueryContactsResponse,
  QueryFacetsOptions,
  QueryFacetsRequest,
  QueryFacetsResponse,
  RestoreInfo,
  Role,
  Search,
  SearchContactsRequest,
  SearchContactsResponse,
  SearchDetails,
  SearchPagingMethodOneOf,
  SegmentsWrapper,
  SessionInfo,
  SortOrder,
  Sorting,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  SubmitOperation,
  SubscriptionStatus,
  SyncSubmitContactRequest,
  SyncSubmitContactResponse,
  UnlabelContactRequest,
  UnlabelContactResponse,
  UpdateContactOptions,
  UpdateContactRequest,
  UpdateContactResponse,
  UpsertContactRequest,
  UpsertContactResponse,
  UpsertContactResponseAction,
  UserInfo,
  WebhookIdentityType,
  utils,
} from './contacts-v4-contact-contacts.universal.js';
